import type { InvoiceField } from '../hooks/useInvoice';

export const INVOICE_TYPE_INDIVIDUAL = 'individual';
export const INVOICE_TYPE_COMPANY = 'company';

// Italian Codice Fiscale placeholder shown in VAT ID input for IT individuals (16 alphanumeric chars)
export const CODICE_FISCALE_PLACEHOLDER = 'AAAAAA00A00A000A';

// Polish tax regulation threshold in grosze (minor units): simplified invoices (NIP without email)
// exceeding 450 PLN require a disclaimer informing users they can contact the operator for a full invoice
export const SIMPLIFIED_INVOICE_THRESHOLD_PLN = 45000;

export const INVOICE_STRATEGY_AFTER_EACH_TRANSACTION = 'after_each_transaction';

// Defines the display order for invoice form fields (lower number = appears first)
export const INVOICE_FIELD_ORDER: Record<string, number> = {
  // `name` (individual) and `companyName` (company) are mutually exclusive per invoice
  // type, so sharing order 1 is unambiguous — only one is ever displayed at a time.
  name: 1,
  companyName: 1,
  personalId: 2,
  regNo: 3,
  taxAdministrationOfficeName: 4,
  country: 5, // Country should come before vatId/taxId
  vatId: 6,
  taxId: 6, // Same priority as vatId
  codiceDestinatario: 7,
  pecDestinatario: 8,
  region: 9,
  city: 10,
  postcode: 11,
  address: 12,
};

// Default order value for invoice fields that don't have an explicit order defined
export const INVOICE_FIELD_DEFAULT_ORDER = 999;

// A tenant may declare the Italy SdI destination fields under the platform's own API names
// instead of the Italian ones. Both spellings address the same logical field, so an alias is
// canonicalised to the FALLBACK_INVOICE_FIELDS name the moment the configuration is read.
// Every downstream SdI rule — ordering, placeholder, format validation, the one-of-two
// requirement and the invoiceDetails mapping — matches on the canonical name only.
export const INVOICE_FIELD_NAME_ALIASES: Record<string, string> = {
  recipientCode: 'codiceDestinatario',
  recipientCertifiedEmail: 'pecDestinatario',
};

// Mapping of country names to ISO codes for VAT validation
export const COUNTRY_NAME_TO_ISO: Record<string, string> = {
  Austria: 'AT',
  Belgium: 'BE',
  Bulgaria: 'BG',
  Croatia: 'HR',
  Cyprus: 'CY',
  'Czech Republic': 'CZ',
  Denmark: 'DK',
  Estonia: 'EE',
  Finland: 'FI',
  France: 'FR',
  Germany: 'DE',
  Greece: 'EL',
  Hungary: 'HU',
  Ireland: 'IE',
  Italy: 'IT',
  Latvia: 'LV',
  Lithuania: 'LT',
  Luxembourg: 'LU',
  Malta: 'MT',
  Netherlands: 'NL',
  Poland: 'PL',
  Portugal: 'PT',
  Romania: 'RO',
  Slovakia: 'SK',
  Slovenia: 'SI',
  Spain: 'ES',
  Sweden: 'SE',
  Switzerland: 'CH',
};

// EU VAT prefixes that differ from the ISO 3166 alpha-2 code. AMPECO validates
// invoiceDetails.country against ISO 3166, so a 2-letter VAT prefix must be corrected
// to its ISO code before submission. Greece: VAT prefix 'EL' vs ISO 'GR' (the only divergence).
export const VAT_PREFIX_TO_ISO: Record<string, string> = {
  EL: 'GR',
};

// Reverse mapping: ISO code to country name (EU countries only - for VAT validation)
export const ISO_TO_COUNTRY_NAME: Record<string, string> = Object.entries(COUNTRY_NAME_TO_ISO).reduce(
  (acc, [countryName, isoCode]) => {
    acc[isoCode] = countryName;
    return acc;
  },
  {} as Record<string, string>
);

// Complete mapping of ALL country ISO codes to names (for dropdown pre-population)
// Includes all countries from the dropdown options, not just EU countries
export const ALL_COUNTRIES_ISO_TO_NAME: Record<string, string> = {
  AF: 'Afghanistan',
  AX: 'Aland Islands',
  AL: 'Albania',
  DZ: 'Algeria',
  AS: 'American Samoa',
  AD: 'Andorra',
  AO: 'Angola',
  AI: 'Anguilla',
  AQ: 'Antarctica',
  AG: 'Antigua and Barbuda',
  AR: 'Argentina',
  AM: 'Armenia',
  AW: 'Aruba',
  AU: 'Australia',
  AT: 'Austria',
  AZ: 'Azerbaijan',
  BS: 'Bahamas',
  BH: 'Bahrain',
  BD: 'Bangladesh',
  BB: 'Barbados',
  BY: 'Belarus',
  BE: 'Belgium',
  BZ: 'Belize',
  BJ: 'Benin',
  BM: 'Bermuda',
  BT: 'Bhutan',
  BO: 'Bolivia',
  BQ: 'Bonaire, Sint Eustatius and Saba',
  BA: 'Bosnia and Herzegovina',
  BW: 'Botswana',
  BV: 'Bouvet Island',
  BR: 'Brazil',
  IO: 'British Indian Ocean Territory',
  BN: 'Brunei Darussalam',
  BG: 'Bulgaria',
  BF: 'Burkina Faso',
  BI: 'Burundi',
  KH: 'Cambodia',
  CM: 'Cameroon',
  CA: 'Canada',
  CV: 'Cape Verde',
  KY: 'Cayman Islands',
  CF: 'Central African Republic',
  TD: 'Chad',
  CL: 'Chile',
  CN: 'China',
  CX: 'Christmas Island',
  CC: 'Cocos (Keeling) Islands',
  CO: 'Colombia',
  KM: 'Comoros',
  CG: 'Congo',
  CD: 'Congo, Democratic Republic',
  CK: 'Cook Islands',
  CR: 'Costa Rica',
  CI: "Cote D'Ivoire",
  HR: 'Croatia',
  CU: 'Cuba',
  CW: 'Curaçao',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DK: 'Denmark',
  DJ: 'Djibouti',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  EC: 'Ecuador',
  EG: 'Egypt',
  SV: 'El Salvador',
  GQ: 'Equatorial Guinea',
  ER: 'Eritrea',
  EE: 'Estonia',
  ET: 'Ethiopia',
  FK: 'Falkland Islands (Malvinas)',
  FO: 'Faroe Islands',
  FJ: 'Fiji',
  FI: 'Finland',
  FR: 'France',
  GF: 'French Guiana',
  PF: 'French Polynesia',
  TF: 'French Southern Territories',
  GA: 'Gabon',
  GM: 'Gambia',
  GE: 'Georgia',
  DE: 'Germany',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GR: 'Greece',
  GL: 'Greenland',
  GD: 'Grenada',
  GP: 'Guadeloupe',
  GU: 'Guam',
  GT: 'Guatemala',
  GG: 'Guernsey',
  GN: 'Guinea',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HT: 'Haiti',
  HM: 'Heard Island and Mcdonald Islands',
  VA: 'Holy See (Vatican City State)',
  HN: 'Honduras',
  HK: 'Hong Kong',
  HU: 'Hungary',
  IS: 'Iceland',
  IN: 'India',
  ID: 'Indonesia',
  IR: 'Iran, Islamic Republic Of',
  IQ: 'Iraq',
  IE: 'Ireland',
  IM: 'Isle Of Man',
  IL: 'Israel',
  IT: 'Italy',
  JM: 'Jamaica',
  JP: 'Japan',
  JE: 'Jersey',
  JO: 'Jordan',
  KZ: 'Kazakhstan',
  KE: 'Kenya',
  KI: 'Kiribati',
  KP: "Korea, Democratic People's Republic Of",
  KR: 'Korea',
  XK: 'Kosovo',
  KW: 'Kuwait',
  KG: 'Kyrgyzstan',
  LA: "Lao People's Democratic Republic",
  LV: 'Latvia',
  LB: 'Lebanon',
  LS: 'Lesotho',
  LR: 'Liberia',
  LY: 'Libya',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MO: 'Macao',
  MK: 'Macedonia',
  MG: 'Madagascar',
  MW: 'Malawi',
  MY: 'Malaysia',
  MV: 'Maldives',
  ML: 'Mali',
  MT: 'Malta',
  MH: 'Marshall Islands',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MU: 'Mauritius',
  YT: 'Mayotte',
  MX: 'Mexico',
  FM: 'Micronesia, Federated States Of',
  MD: 'Moldova',
  MC: 'Monaco',
  MN: 'Mongolia',
  ME: 'Montenegro',
  MS: 'Montserrat',
  MA: 'Morocco',
  MZ: 'Mozambique',
  MM: 'Myanmar',
  NA: 'Namibia',
  NR: 'Nauru',
  NP: 'Nepal',
  NL: 'Netherlands',
  NC: 'New Caledonia',
  NZ: 'New Zealand',
  NI: 'Nicaragua',
  NE: 'Niger',
  NG: 'Nigeria',
  NU: 'Niue',
  NF: 'Norfolk Island',
  MP: 'Northern Mariana Islands',
  NO: 'Norway',
  OM: 'Oman',
  PK: 'Pakistan',
  PW: 'Palau',
  PS: 'Palestinian Territory, Occupied',
  PA: 'Panama',
  PG: 'Papua New Guinea',
  PY: 'Paraguay',
  PE: 'Peru',
  PH: 'Philippines',
  PN: 'Pitcairn',
  PL: 'Poland',
  PT: 'Portugal',
  PR: 'Puerto Rico',
  QA: 'Qatar',
  RE: 'Reunion',
  RO: 'Romania',
  RU: 'Russian Federation',
  RW: 'Rwanda',
  BL: 'Saint Barthelemy',
  SH: 'Saint Helena',
  KN: 'Saint Kitts and Nevis',
  LC: 'Saint Lucia',
  MF: 'Saint Martin',
  PM: 'Saint Pierre and Miquelon',
  VC: 'Saint Vincent and Grenadines',
  WS: 'Samoa',
  SM: 'San Marino',
  ST: 'Sao Tome and Principe',
  SA: 'Saudi Arabia',
  SN: 'Senegal',
  RS: 'Serbia',
  SC: 'Seychelles',
  SL: 'Sierra Leone',
  SG: 'Singapore',
  SX: 'Sint Maarten (Dutch part)',
  SK: 'Slovakia',
  SI: 'Slovenia',
  SB: 'Solomon Islands',
  SO: 'Somalia',
  ZA: 'South Africa',
  GS: 'South Georgia and Sandwich Isl',
  SS: 'South Sudan',
  ES: 'Spain',
  LK: 'Sri Lanka',
  SD: 'Sudan',
  SR: 'Suriname',
  SJ: 'Svalbard and Jan Mayen',
  SZ: 'Swaziland',
  SE: 'Sweden',
  CH: 'Switzerland',
  SY: 'Syrian Arab Republic',
  TW: 'Taiwan',
  TJ: 'Tajikistan',
  TZ: 'Tanzania',
  TH: 'Thailand',
  TL: 'Timor-Leste',
  TG: 'Togo',
  TK: 'Tokelau',
  TO: 'Tonga',
  TT: 'Trinidad and Tobago',
  TN: 'Tunisia',
  TR: 'Turkey',
  TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands',
  TV: 'Tuvalu',
  UG: 'Uganda',
  UA: 'Ukraine',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  UM: 'United States Outlying Islands',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VU: 'Vanuatu',
  VE: 'Venezuela',
  VN: 'Vietnam',
  VG: 'Virgin Islands, British',
  VI: 'Virgin Islands, US',
  WF: 'Wallis and Futuna',
  EH: 'Western Sahara',
  YE: 'Yemen',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

// Reverse of ALL_COUNTRIES_ISO_TO_NAME: country name -> ISO code. Built once at module load
// so name resolution is O(1); first ISO code wins if a name were ever to repeat.
export const ALL_COUNTRIES_NAME_TO_ISO: Record<string, string> = Object.entries(ALL_COUNTRIES_ISO_TO_NAME).reduce(
  (acc, [isoCode, countryName]) => {
    if (!(countryName in acc)) acc[countryName] = isoCode;
    return acc;
  },
  {} as Record<string, string>
);

export const STATES_BY_COUNTRY = {
  Australia: {
    NSW: 'New South Wales (AU-NSW)',
    VIC: 'Victoria (AU-VIC)',
    QLD: 'Queensland (AU-QLD)',
    WA: 'Western Australia (AU-WA)',
    SA: 'South Australia (AU-SA)',
    TAS: 'Tasmania (AU-TAS)',
    ACT: 'Australian Capital Territory (AU-ACT)',
    NT: 'Northern Territory (AU-NT)',
    JBT: 'Jervis Bay Territory (AU-JBT)',
    CX: 'Christmas Island (AU-CX)',
    NF: 'Norfolk Island (AU-NF)',
    CC: 'Cocos (Keeling) Islands (AU-CC)',
    AQ: 'Australian Antarctic Territory (AU-AQ)',
    CSI: 'Coral Sea Islands (AU-CSI)',
    ACI: 'Ashmore and Cartier Islands (AU-ACI)',
    HM: 'Heard Island and McDonald Islands (AU-HM)',
  },
  'United States': {
    AL: 'Alabama (US-AL)',
    AK: 'Alaska (US-AK)',
    AS: 'American Samoa (US-AS)',
    AZ: 'Arizona (US-AZ)',
    AR: 'Arkansas (US-AR)',
    CA: 'California (US-CA)',
    CO: 'Colorado (US-CO)',
    CT: 'Connecticut (US-CT)',
    DE: 'Delaware (US-DE)',
    DC: 'District Of Columbia (US-DC)',
    FL: 'Florida (US-FL)',
    GA: 'Georgia (US-GA)',
    GU: 'Guam (US-GU)',
    HI: 'Hawaii (US-HI)',
    ID: 'Idaho (US-ID)',
    IL: 'Illinois (US-IL)',
    IN: 'Indiana (US-IN)',
    IA: 'Iowa (US-IA)',
    KS: 'Kansas (US-KS)',
    KY: 'Kentucky (US-KS)',
    LA: 'Louisiana (US-LA)',
    ME: 'Maine (US-ME)',
    MD: 'Maryland (US-MD)',
    MA: 'Massachusetts (US-MA)',
    MI: 'Michigan (US-MI)',
    MN: 'Minnesota (US-MN)',
    MS: 'Mississippi (US-MS)',
    MO: 'Missouri (US-MO)',
    MT: 'Montana (US-MT)',
    NE: 'Nebraska (US-NE)',
    NV: 'Nevada (US-NV)',
    NH: 'New Hampshire (US-NH)',
    NJ: 'New Jersey (US-NJ)',
    NM: 'New Mexico (US-NM)',
    NY: 'New York (US-NY)',
    NC: 'North Carolina (US-NC)',
    ND: 'North Dakota (US-ND)',
    MP: 'Northern Mariana Islands (US-MP)',
    OH: 'Ohio (US-OH)',
    OK: 'Oklahoma (US-OK)',
    OR: 'Oregon (US-OR)',
    PA: 'Pennsylvania (US-PA)',
    PR: 'Puerto Rico (US-PR)',
    RI: 'Rhode Island (US-RI)',
    SC: 'South Carolina (US-SC)',
    SD: 'South Dakota (US-SD)',
    TN: 'Tennessee (US-TN)',
    TX: 'Texas (US-TX)',
    UT: 'Utah (US-UT)',
    VT: 'Vermont (US-VT)',
    VI: 'Virgin Islands (US-VI)',
    VA: 'Virginia (US-VA)',
    WA: 'Washington (US-WA)',
    WV: 'West Virginia (US-WV)',
    WI: 'Wisconsin (US-WI)',
    WY: 'Wyoming (US-WY)',
  },
  Canada: {
    AB: 'Alberta (CA-AB)',
    BC: 'British Columbia (CA-BC)',
    MB: 'Manitoba (CA-MB)',
    NB: 'New Brunswick (CA-NB)',
    NL: 'Newfoundland and Labrador (CA-NL)',
    NT: 'Northwest Territories (CA-NT)',
    NS: 'Nova Scotia (CA-NS)',
    NU: 'Nunavut (CA-NU)',
    ON: 'Ontario (CA-ON)',
    PE: 'Prince Edward Island (CA-PE)',
    QC: 'Quebec (CA-QC)',
    SK: 'Saskatchewan (CA-SK)',
    YT: 'Yukon (CA-YT)',
  },
  'United States Outlying Islands': {
    '81': 'Baker Island (UM-81)',
    '84': 'Howland Island (UM-84)',
    '85': 'Jarvis Island (UM-85)',
    '67': 'Johnston Atoll (UM-67)',
    '89': 'Kingman Reef (UM-89)',
    '71': 'Midway Atoll (UM-71)',
    '76': 'Navassa Island (UM-76)',
    '95': 'Palmyra Atoll (UM-95)',
    '79': 'Wake Island (UM-79)',
  },
  Romania: {
    AB: 'Alba (RO-AB)',
    AR: 'Arad (RO-AR)',
    AG: 'Argeș (RO-AG)',
    BC: 'Bacău (RO-BC)',
    BH: 'Bihor (RO-BH)',
    BN: 'Bistrița-Năsăud (RO-BN)',
    BT: 'Botoșani (RO-BT)',
    BV: 'Brașov (RO-BV)',
    BR: 'Brăila (RO-BR)',
    B: 'București (RO-B)',
    BZ: 'Buzău (RO-BZ)',
    CL: 'Călărași (RO-CL)',
    CS: 'Caraș-Severin (RO-CS)',
    CJ: 'Cluj (RO-CJ)',
    CT: 'Constanța (RO-CT)',
    CV: 'Covasna (RO-CV)',
    DB: 'Dâmbovița (RO-DB)',
    DJ: 'Dolj (RO-DJ)',
    GL: 'Galați (RO-GL)',
    GR: 'Giurgiu (RO-GR)',
    GJ: 'Gorj (RO-GJ)',
    HR: 'Harghita (RO-HR)',
    HD: 'Hunedoara (RO-HD)',
    IL: 'Ialomița (RO-IL)',
    IS: 'Iași (RO-IS)',
    IF: 'Ilfov (RO-IF)',
    MM: 'Maramureș (RO-MM)',
    MH: 'Mehedinți (RO-MH)',
    MS: 'Mures (RO-MS)',
    NT: 'Neamț (RO-NT)',
    OT: 'Olt (RO-OT)',
    PH: 'Prahova (RO-PH)',
    SM: 'Satu Mare (RO-SM)',
    SJ: 'Sălaj (RO-SJ)',
    SB: 'Sibiu (RO-SB)',
    SV: 'Suceava (RO-SV)',
    TR: 'Teleorman (RO-TR)',
    TM: 'Timiș (RO-TM)',
    TL: 'Tulcea (RO-TL)',
    VS: 'Vaslui (RO-VS)',
    VL: 'Vâlcea (RO-VL)',
    VN: 'Vrancea (RO-VN)',
  },
};

export const FALLBACK_INVOICE_FIELDS: InvoiceField[] = [
  {
    name: 'name',
    type: 'text',
    required: true,
    disabled: false,
    label: 'Name',
    placeholder: 'Enter your name',
  },
  {
    name: 'personalId',
    type: 'text',
    required: false,
    disabled: false,
    label: 'Personal ID',
    placeholder: 'Enter your personal ID',
  },
  {
    name: 'regNo',
    type: 'text',
    required: false,
    disabled: false,
    label: 'Registration Number',
    placeholder: 'Enter registration number',
    invoiceType: 'company',
  },
  {
    name: 'taxAdministrationOfficeName',
    type: 'text',
    required: false,
    disabled: false,
    label: 'Tax Administration Office Name',
    placeholder: 'Enter tax administration office name',
    invoiceType: 'company',
  },
  {
    name: 'country',
    type: 'select',
    required: true,
    disabled: false,
    label: 'Country',
    placeholder: 'Select country',
    options: [
      'Afghanistan',
      'Aland Islands',
      'Albania',
      'Algeria',
      'American Samoa',
      'Andorra',
      'Angola',
      'Anguilla',
      'Antarctica',
      'Antigua and Barbuda',
      'Argentina',
      'Armenia',
      'Aruba',
      'Australia',
      'Austria',
      'Azerbaijan',
      'Bahamas',
      'Bahrain',
      'Bangladesh',
      'Barbados',
      'Belarus',
      'Belgium',
      'Belize',
      'Benin',
      'Bermuda',
      'Bhutan',
      'Bolivia',
      'Bonaire, Sint Eustatius and Saba',
      'Bosnia and Herzegovina',
      'Botswana',
      'Bouvet Island',
      'Brazil',
      'British Indian Ocean Territory',
      'Brunei Darussalam',
      'Bulgaria',
      'Burkina Faso',
      'Burundi',
      'Cambodia',
      'Cameroon',
      'Canada',
      'Cape Verde',
      'Cayman Islands',
      'Central African Republic',
      'Chad',
      'Chile',
      'China',
      'Christmas Island',
      'Cocos (Keeling) Islands',
      'Colombia',
      'Comoros',
      'Congo',
      'Congo, Democratic Republic',
      'Cook Islands',
      'Costa Rica',
      "Cote D'Ivoire",
      'Croatia',
      'Cuba',
      'Curaçao',
      'Cyprus',
      'Czech Republic',
      'Denmark',
      'Djibouti',
      'Dominica',
      'Dominican Republic',
      'Ecuador',
      'Egypt',
      'El Salvador',
      'Equatorial Guinea',
      'Eritrea',
      'Estonia',
      'Ethiopia',
      'Falkland Islands (Malvinas)',
      'Faroe Islands',
      'Fiji',
      'Finland',
      'France',
      'French Guiana',
      'French Polynesia',
      'French Southern Territories',
      'Gabon',
      'Gambia',
      'Georgia',
      'Germany',
      'Ghana',
      'Gibraltar',
      'Greece',
      'Greenland',
      'Grenada',
      'Guadeloupe',
      'Guam',
      'Guatemala',
      'Guernsey',
      'Guinea',
      'Guinea-Bissau',
      'Guyana',
      'Haiti',
      'Heard Island and Mcdonald Islands',
      'Holy See (Vatican City State)',
      'Honduras',
      'Hong Kong',
      'Hungary',
      'Iceland',
      'India',
      'Indonesia',
      'Iran, Islamic Republic Of',
      'Iraq',
      'Ireland',
      'Isle Of Man',
      'Israel',
      'Italy',
      'Jamaica',
      'Japan',
      'Jersey',
      'Jordan',
      'Kazakhstan',
      'Kenya',
      'Kiribati',
      "Korea, Democratic People's Republic Of",
      'Korea',
      'Kosovo',
      'Kuwait',
      'Kyrgyzstan',
      "Lao People's Democratic Republic",
      'Latvia',
      'Lebanon',
      'Lesotho',
      'Liberia',
      'Libya',
      'Liechtenstein',
      'Lithuania',
      'Luxembourg',
      'Macao',
      'Macedonia',
      'Madagascar',
      'Malawi',
      'Malaysia',
      'Maldives',
      'Mali',
      'Malta',
      'Marshall Islands',
      'Martinique',
      'Mauritania',
      'Mauritius',
      'Mayotte',
      'Mexico',
      'Micronesia, Federated States Of',
      'Moldova',
      'Monaco',
      'Mongolia',
      'Montenegro',
      'Montserrat',
      'Morocco',
      'Mozambique',
      'Myanmar',
      'Namibia',
      'Nauru',
      'Nepal',
      'Netherlands',
      'New Caledonia',
      'New Zealand',
      'Nicaragua',
      'Niger',
      'Nigeria',
      'Niue',
      'Norfolk Island',
      'Northern Mariana Islands',
      'Norway',
      'Oman',
      'Pakistan',
      'Palau',
      'Palestinian Territory, Occupied',
      'Panama',
      'Papua New Guinea',
      'Paraguay',
      'Peru',
      'Philippines',
      'Pitcairn',
      'Poland',
      'Portugal',
      'Puerto Rico',
      'Qatar',
      'Reunion',
      'Romania',
      'Russian Federation',
      'Rwanda',
      'Saint Barthelemy',
      'Saint Helena',
      'Saint Kitts and Nevis',
      'Saint Lucia',
      'Saint Martin',
      'Saint Pierre and Miquelon',
      'Saint Vincent and Grenadines',
      'Samoa',
      'San Marino',
      'Sao Tome and Principe',
      'Saudi Arabia',
      'Senegal',
      'Serbia',
      'Seychelles',
      'Sierra Leone',
      'Singapore',
      'Sint Maarten (Dutch part)',
      'Slovakia',
      'Slovenia',
      'Solomon Islands',
      'Somalia',
      'South Africa',
      'South Georgia and Sandwich Isl',
      'South Sudan',
      'Spain',
      'Sri Lanka',
      'Sudan',
      'Suriname',
      'Svalbard and Jan Mayen',
      'Swaziland',
      'Sweden',
      'Switzerland',
      'Syrian Arab Republic',
      'Taiwan',
      'Tajikistan',
      'Tanzania',
      'Thailand',
      'Timor-Leste',
      'Togo',
      'Tokelau',
      'Tonga',
      'Trinidad and Tobago',
      'Tunisia',
      'Turkey',
      'Turkmenistan',
      'Turks and Caicos Islands',
      'Tuvalu',
      'Uganda',
      'Ukraine',
      'United Arab Emirates',
      'United Kingdom',
      'United States',
      'United States Outlying Islands',
      'Uruguay',
      'Uzbekistan',
      'Vanuatu',
      'Venezuela',
      'Vietnam',
      'Virgin Islands, British',
      'Virgin Islands, US',
      'Wallis and Futuna',
      'Western Sahara',
      'Yemen',
      'Zambia',
      'Zimbabwe',
    ],
  },
  {
    name: 'vatId',
    type: 'text',
    required: true,
    disabled: false,
    label: 'Tax ID / VAT ID',
    placeholder: 'Enter your VAT ID',
  },
  // Italy SdI destination fields are metadata-only: they enrich a tenant-declared field with type,
  // label and placeholder, but must not render when the tenant declared no fields at all. The
  // zero-config form renders FALLBACK_DISPLAY_INVOICE_FIELDS (src/screens/RequestInvoice/constants.ts),
  // which filters them out; any further metadata-only field added here belongs in that filter too.
  {
    name: 'codiceDestinatario',
    type: 'text',
    required: false,
    disabled: false,
    label: 'Codice Destinatario',
    placeholder: 'ABC1234',
    invoiceType: 'company',
  },
  {
    name: 'pecDestinatario',
    type: 'email',
    required: false,
    disabled: false,
    label: 'Indirizzo PEC',
    placeholder: 'nome@pec.it',
    invoiceType: 'company',
  },
  {
    name: 'region',
    type: 'select',
    required: false,
    disabled: false,
    label: 'State / Region',
    placeholder: 'Select state',
    options: ['Please select'],
  },
  {
    name: 'city',
    type: 'text',
    required: false,
    disabled: false,
    label: 'City',
    placeholder: 'Enter your city',
  },
  {
    name: 'postcode',
    type: 'text',
    required: false,
    disabled: false,
    label: 'Postcode',
    placeholder: 'Enter your postcode',
  },
  {
    name: 'address',
    type: 'text',
    required: false,
    disabled: false,
    label: 'Address',
    placeholder: 'Enter your address',
  },
];
